/**
 * Credit Memo Section (SPRINT-25 F008 + GP F008 Auto-Content UX)
 *
 * Individual collapsible section within the credit memo editor.
 * Features:
 * - Accordion item with section title
 * - Textarea for markdown content
 * - "Mark as Complete" checkbox
 * - Auto-save on blur (debounced)
 * - Toast notification on save
 * - F008: Auto-generated badge, background tint, review warning,
 *   context menu with "Clear auto-content", edit mode banner
 */

import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, MoreVertical, AlertTriangle, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { DealCreditMemoSection } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreditMemoSectionHandle {
  flushPendingSave: () => void;
}

interface CreditMemoSectionProps {
  section: DealCreditMemoSection;
  dealId: string;
  memoId: string;
  onSectionUpdated: (updated: DealCreditMemoSection) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CreditMemoSection = forwardRef<CreditMemoSectionHandle, CreditMemoSectionProps>(function CreditMemoSection({
  section,
  dealId,
  memoId,
  onSectionUpdated,
}, ref) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [content, setContent] = useState(section.content ?? "");
  const [isComplete, setIsComplete] = useState(section.isComplete ?? false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAutoGenerated = section.autoGenerated === true;

  // -----------------------------------------------------------------------
  // Patch section mutation
  // -----------------------------------------------------------------------

  const patchMutation = useMutation({
    mutationFn: async (data: Partial<DealCreditMemoSection>) => {
      const response = await apiRequest(
        "PATCH",
        `/api/financial/deals/${dealId}/credit-memos/${memoId}/sections/${section.id}`,
        data,
      );
      return (await response.json()) as DealCreditMemoSection;
    },
    onSuccess: (updated: DealCreditMemoSection) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos/${memoId}`],
      });
      onSectionUpdated(updated);
      toast({
        title: "Section saved",
        description: `"${section.sectionTitle}" updated.`,
        duration: 2000,
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Could not save section. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Expose flush method to parent so pending debounced saves can be
  // flushed synchronously before auto-populate resets local state.
  useImperativeHandle(ref, () => ({
    flushPendingSave() {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (content !== (section.content ?? "")) {
        patchMutation.mutate({ content: content || null });
      }
    },
  }), [content, section.content, patchMutation]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  function handleContentChange(value: string) {
    setContent(value);
  }

  function handleContentBlur() {
    if (content !== (section.content ?? "")) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        patchMutation.mutate({ content: content || null });
      }, 300);
    }
  }

  function handleCompleteChange(checked: boolean) {
    setIsComplete(checked);
    patchMutation.mutate({ isComplete: checked });
  }

  function handleClearAutoContent() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setContent("");
    patchMutation.mutate({ content: null });
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <AccordionItem
      value={section.sectionType}
      data-testid={`section-${section.sectionType}`}
      data-auto-generated={isAutoGenerated ? "true" : "false"}
      className={`border rounded-lg px-4 ${isAutoGenerated ? "bg-amber-50 border-amber-200" : ""}`}
    >
      <AccordionTrigger
        className="hover:no-underline py-3"
        data-testid={`section-trigger-${section.sectionType}`}
      >
        <div className="flex items-center gap-3 text-left flex-1">
          {isComplete ? (
            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
          ) : (
            <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
          <span className="font-medium text-sm">{section.sectionTitle}</span>
          {/* F008 AC001: Auto-generated badge */}
          {isAutoGenerated && (
            <Badge
              variant="outline"
              data-testid="badge-auto-generated"
              className="border-amber-400 text-amber-700 text-xs ml-1"
            >
              Auto-generated
            </Badge>
          )}
          {isComplete && (
            <Badge
              variant="secondary"
              className="text-xs bg-green-50 text-green-700 border-green-200 ml-auto mr-2"
            >
              Complete
            </Badge>
          )}
          {/* F008 AC005: Context menu for auto-generated sections */}
          {isAutoGenerated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-1"
                  data-testid={`section-menu-${section.sectionType}`}
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid="menu-clear-auto-content"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAutoContent();
                  }}
                >
                  Clear auto-generated content
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </AccordionTrigger>

      <AccordionContent className="pt-2 pb-4 space-y-3">
        {/* F008 AC006: Edit banner for auto-generated sections */}
        {isAutoGenerated && (
          <div
            data-testid="banner-editing-auto"
            className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 rounded px-3 py-2"
          >
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
            This section contains auto-generated content. Saving changes will mark it as manually reviewed.
          </div>
        )}

        <Textarea
          data-testid={`textarea-section-${section.id}`}
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          onBlur={handleContentBlur}
          placeholder={`Add ${section.sectionTitle.toLowerCase()} commentary...`}
          className="min-h-[120px] text-sm font-mono resize-y"
          disabled={patchMutation.isPending}
        />

        {/* F008 AC003: Review required indicator */}
        {isAutoGenerated && (
          <div
            data-testid="review-required-warning"
            className="flex items-center gap-1.5 text-xs text-amber-600"
          >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            This content was auto-generated. Review and edit before finalising.
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id={`complete-${section.id}`}
            data-testid={`checkbox-complete-${section.id}`}
            checked={isComplete}
            onCheckedChange={(checked) =>
              handleCompleteChange(checked === true)
            }
            disabled={patchMutation.isPending}
          />
          <Label
            htmlFor={`complete-${section.id}`}
            className="text-sm cursor-pointer text-muted-foreground"
          >
            Mark as Complete
          </Label>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
});
